import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {useAnalytics} from '@github-ui/use-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback, useMemo} from 'react'
import {FINDINGS_ANALYTICS_EVENTS} from './analytics'
import {
  AUTHOR_IMPLIED_SOURCE_LABELS,
  getFindingSeverity,
  getFindingSourceAuthors,
  getFindingSourceLabels,
  isNeedsAttention,
  SEVERITY_ORDER,
} from './entity-helpers'
import {filtersStorageKey} from './storage-keys'
import type {Finding} from './types'

export type FindingsView = 'attention' | 'open' | 'resolved' | 'outdated'
export type SourceFilterType = 'source' | 'source-author'

export interface SourceFilterOption {
  type: SourceFilterType
  key: string
  label: string
  value: string
}

export type FilterFindingsState = ReturnType<typeof useFilterFindings>

const FINDINGS_VIEWS: ReadonlySet<FindingsView> = new Set(['attention', 'open', 'resolved', 'outdated'])
const FINDINGS_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(SEVERITY_ORDER)

interface PersistedFilterFindingsState {
  selectedView: FindingsView
  selectedSeverities: RuleSeverity[]
  selectedSources: string[]
  selectedSourceAuthors: string[]
}

const DEFAULT_FILTER_STATE: PersistedFilterFindingsState = {
  selectedView: 'attention',
  selectedSeverities: [],
  selectedSources: [],
  selectedSourceAuthors: [],
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const uniqueStrings = (values: unknown): string[] => {
  if (!Array.isArray(values)) return []

  const unique = new Set<string>()
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      unique.add(value)
    }
  }
  return [...unique]
}

const normalizeFilterState = (value: unknown): PersistedFilterFindingsState => {
  if (!isRecord(value)) return DEFAULT_FILTER_STATE

  const selectedView = FINDINGS_VIEWS.has(value.selectedView as FindingsView)
    ? (value.selectedView as FindingsView)
    : DEFAULT_FILTER_STATE.selectedView
  const selectedSeverities = uniqueStrings(value.selectedSeverities).filter((severity): severity is RuleSeverity =>
    FINDINGS_SEVERITIES.has(severity as RuleSeverity),
  )
  const selectedSources = uniqueStrings(value.selectedSources)
  const selectedSourceAuthors = uniqueStrings(value.selectedSourceAuthors).map(login => login.toLowerCase())

  return {selectedView, selectedSeverities, selectedSources, selectedSourceAuthors}
}

const toggleValue = <T extends string>(values: T[], value: T): T[] => {
  const next = new Set(values)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return [...next]
}

const sourceFilterKey = (type: SourceFilterType, value: string): string => `${type}:${value.toLowerCase()}`

export const useFilterFindings = (
  openFindings: Finding[],
  ignoredFindings: Finding[],
  outdatedFindings: Finding[],
  pullRequestBasePath: string,
) => {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const [storedFilterState, setStoredFilterState] = useSessionStorage<PersistedFilterFindingsState>(
    filtersStorageKey(pullRequestBasePath),
    DEFAULT_FILTER_STATE,
  )
  const filterState = useMemo(() => normalizeFilterState(storedFilterState), [storedFilterState])
  const selectedView = filterState.selectedView
  const selectedSeverities = useMemo(() => new Set(filterState.selectedSeverities), [filterState.selectedSeverities])
  const selectedSourceLabels = useMemo(() => new Set(filterState.selectedSources), [filterState.selectedSources])
  const selectedSourceAuthorKeys = useMemo(
    () => new Set(filterState.selectedSourceAuthors.map(login => login.toLowerCase())),
    [filterState.selectedSourceAuthors],
  )
  const selectedSources = useMemo(
    () =>
      new Set([
        ...filterState.selectedSources.map(source => sourceFilterKey('source', source)),
        ...filterState.selectedSourceAuthors.map(sourceAuthor => sourceFilterKey('source-author', sourceAuthor)),
      ]),
    [filterState.selectedSources, filterState.selectedSourceAuthors],
  )

  const allFindings = useMemo(
    () => [...openFindings, ...ignoredFindings, ...outdatedFindings],
    [openFindings, ignoredFindings, outdatedFindings],
  )

  const availableSeverities = useMemo(() => {
    const severities = new Set<RuleSeverity>()
    for (const f of allFindings) {
      const s = getFindingSeverity(f)
      if (s) severities.add(s)
    }
    return SEVERITY_ORDER.filter(s => severities.has(s))
  }, [allFindings])

  const findingsForSelectedView = useMemo(() => {
    if (selectedView === 'resolved') return ignoredFindings
    if (selectedView === 'outdated') return outdatedFindings
    if (selectedView === 'attention') return openFindings.filter(isNeedsAttention)
    return openFindings.filter(f => !isNeedsAttention(f))
  }, [selectedView, openFindings, ignoredFindings, outdatedFindings])

  const availableSources = useMemo(() => {
    const sources = new Set<string>()
    const sourceAuthors = new Map<string, string>()

    for (const f of findingsForSelectedView) {
      for (const label of getFindingSourceLabels(f)) {
        sources.add(label)
      }

      for (const {login} of getFindingSourceAuthors(f)) {
        const key = login.toLowerCase()
        if (AUTHOR_IMPLIED_SOURCE_LABELS.has(key)) continue
        if (!sourceAuthors.has(key)) sourceAuthors.set(key, login)
      }
    }

    const sourceOptions = [...sources].sort().map<SourceFilterOption>(source => ({
      type: 'source',
      key: sourceFilterKey('source', source),
      label: source,
      value: source,
    }))
    // Exclude authors already represented by a source label
    const sourceAuthorOptions = [...sourceAuthors.values()]
      .filter(login => !sources.has(login))
      .sort((a, b) => a.localeCompare(b))
      .map<SourceFilterOption>(sourceAuthor => ({
        type: 'source-author',
        key: sourceFilterKey('source-author', sourceAuthor),
        label: sourceAuthor,
        value: sourceAuthor,
      }))

    return [...sourceOptions, ...sourceAuthorOptions]
  }, [findingsForSelectedView])

  const updateFilterState = useCallback(
    (update: (current: PersistedFilterFindingsState) => PersistedFilterFindingsState) => {
      setStoredFilterState(prev => update(normalizeFilterState(prev)))
    },
    [setStoredFilterState],
  )

  const handleViewChange = useCallback(
    (view: FindingsView) => {
      updateFilterState(current => ({...current, selectedView: view, selectedSources: [], selectedSourceAuthors: []}))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'view',
        view,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleSeverityToggle = useCallback(
    (severity: RuleSeverity) => {
      updateFilterState(current => ({
        ...current,
        selectedSeverities: toggleValue(current.selectedSeverities, severity),
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'severity',
        severity,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleSourceToggle = useCallback(
    (sourceFilter: SourceFilterOption) => {
      updateFilterState(current => ({
        ...current,
        selectedSources:
          sourceFilter.type === 'source'
            ? toggleValue(current.selectedSources, sourceFilter.value)
            : current.selectedSources,
        selectedSourceAuthors:
          sourceFilter.type === 'source-author'
            ? toggleValue(current.selectedSourceAuthors, sourceFilter.value.toLowerCase())
            : current.selectedSourceAuthors,
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'source',
        source_type: sourceFilter.type,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleClearFilters = useCallback(() => {
    updateFilterState(current => ({...current, selectedSeverities: [], selectedSources: [], selectedSourceAuthors: []}))
    sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
      filter_type: 'clear',
    })
  }, [updateFilterState, sendAnalyticsEvent])

  const findingMatchesSelectedFilters = useCallback(
    (finding: Finding) => {
      if (selectedSeverities.size > 0) {
        const severity = getFindingSeverity(finding)
        if (!severity || !selectedSeverities.has(severity)) return false
      }

      if (selectedSourceLabels.size > 0) {
        const labels = getFindingSourceLabels(finding)
        if (!labels.some(label => selectedSourceLabels.has(label))) return false
      }

      if (selectedSourceAuthorKeys.size > 0) {
        const sourceAuthors = getFindingSourceAuthors(finding)
        if (!sourceAuthors.some(({login}) => selectedSourceAuthorKeys.has(login.toLowerCase()))) return false
      }

      return true
    },
    [selectedSeverities, selectedSourceLabels, selectedSourceAuthorKeys],
  )

  const displayedFindings = useMemo(
    () => findingsForSelectedView.filter(findingMatchesSelectedFilters),
    [findingsForSelectedView, findingMatchesSelectedFilters],
  )

  const counts = useMemo(() => {
    const attention = openFindings.filter(isNeedsAttention).length
    const other = openFindings.length - attention
    const ignored = ignoredFindings.length
    const outdated = outdatedFindings.length
    const displayed = displayedFindings.length
    const filteredOpenFindings = openFindings.filter(findingMatchesSelectedFilters)
    const filteredAttention = filteredOpenFindings.filter(isNeedsAttention).length
    const filteredOther = filteredOpenFindings.length - filteredAttention
    const filteredIgnored = ignoredFindings.filter(findingMatchesSelectedFilters).length
    const filteredOutdated = outdatedFindings.filter(findingMatchesSelectedFilters).length

    return {
      attentionCount: attention,
      otherCount: other,
      ignoredCount: ignored,
      outdatedCount: outdated,
      displayedCount: displayed,
      filteredAttentionCount: filteredAttention,
      filteredOtherCount: filteredOther,
      filteredIgnoredCount: filteredIgnored,
      filteredOutdatedCount: filteredOutdated,
    }
  }, [openFindings, ignoredFindings, outdatedFindings, displayedFindings, findingMatchesSelectedFilters])

  return {
    selectedView,
    handleViewChange,
    availableSeverities,
    availableSources,
    selectedSeverities,
    selectedSources,
    handleSeverityToggle,
    handleSourceToggle,
    handleClearFilters,
    displayedFindings,
    ...counts,
  }
}
